// src/contexts/AuthContext.jsx

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Configure axios defaults
const resolveApiUrl = () => {
  const configured = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  if (configured) return configured;

  if (typeof window !== 'undefined') {
    const { protocol, hostname, origin } = window.location;
    const isLocal =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0';

    if (!isLocal && protocol.startsWith('http')) {
      return `${origin}/api`;
    }
  }

  return 'http://localhost:8000/api';
};

const API_URL = resolveApiUrl();

axios.defaults.baseURL = API_URL;
axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.headers.common['Content-Type'] = 'application/json';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Logout
  const logout = useCallback(async () => {
    try {
      if (token) {
        await axios.post('/auth/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Fetch current user
  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get('/auth/me');
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // Set token in axios headers
  useEffect(() => {
    if (!token) {
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
      return;
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchUser();
  }, [token, fetchUser]);

  // Login
  const login = async (email, password) => {
    try {
      const response = await axios.post('/auth/login', { email, password });
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);
      
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  // Register
  const register = async (name, email, password, password_confirmation) => {
    try {
      const response = await axios.post('/auth/register', {
        name,
        email,
        password,
        password_confirmation
      });
      
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);
      
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed',
        errors: error.response?.data?.errors
      };
    }
  };

  // Role checking
  const hasRole = (role) => {
    if (!user || !user.roles) return false;
    return user.roles.some(r => r.slug === role);
  };

  const hasAnyRole = (roles) => {
    if (!user || !user.roles) return false;
    return user.roles.some(r => roles.includes(r.slug));
  };

  const isAdmin = () => {
    return hasAnyRole(['super-admin', 'admin']);
  };

  const isSuperAdmin = () => {
    return hasRole('super-admin');
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser: fetchUser,
    hasRole,
    hasAnyRole,
    isAdmin,
    isSuperAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
